// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import { type FetchBaseQueryError } from '@reduxjs/toolkit/query';
import { brokerageApi } from '@shared/data-access/api-base/citec-brokerage-api';
import type {
  CurrencyPairs,
  ExchangeRatesQueryReturn,
} from '@shared/data-access/interfaces/api/brokerage/currency';
import type { ExchangeRatesState } from '@shared/data-access/interfaces/redux/assets-repository-state-types';
import {
  assetsRepositorySlice,
  selectRatesByCurrencyPairs,
} from '@shared/data-access/slices/assets-repository';
import { isTest } from '@shared/environment';

export const currencyApi = brokerageApi.injectEndpoints({
  endpoints: build => ({
    currencyPrices: build.query<ExchangeRatesState[], CurrencyPairs>({
      queryFn: async (
        payload,
        { getState, dispatch },
        _extraOptions,
        fetchWithBQ,
      ) => {
        /* istanbul ignore if */
        if (!payload.length) return { data: [] };

        /* istanbul ignore next */
        const cachedRates = isTest
          ? []
          : selectRatesByCurrencyPairs(getState() as any, payload);

        const missingPairs = payload.filter(
          pair => !cachedRates.find(rate => rate.pair === pair),
        );

        /* istanbul ignore if */
        if (!missingPairs.length) {
          return { data: cachedRates };
        }

        const response = (await fetchWithBQ({
          url: '/v2/currency/prices/',
          method: 'POST',
          body: missingPairs,
        })) as ExchangeRatesQueryReturn;

        if (response.error)
          return {
            error: response.error as FetchBaseQueryError,
          };

        const transformedRates: ExchangeRatesState[] = response.data.map(item => {
          const [pair, rate] = Object.entries(item)[0];
          return { pair, rate };
        });

        const data = transformedRates.concat(cachedRates);

        dispatch(assetsRepositorySlice.actions.syncRates(data));

        return { data: data, meta: response.meta };
      },

      providesTags: [{ type: 'Currency', id: 'Prices' }],
    }),
  }),
});
